import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { UnitTransferDocumentTypeEnumType } from '__generated__/graphql';
import { FC, useRef, useState } from 'react';

import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogTitle } from 'primitives/Dialog';

import { FormInput, FormPanel } from 'components/FormPanel';

import unitTransferDocumentTypeToReadable from 'utils/enums/unit-transfer-document-type-to-readable';

const ADD_UNIT_TRANSFER_CHECKLIST_ITEM_MUTATION = gql(`
  mutation AddUnitTransferChecklistItem($requestId: ID!, $type: UnitTransferDocumentTypeEnumType!) {
    addUnitTransferChecklistItem(requestId: $requestId, type: $type) {
      id
      type
      name
      status
      fulfilmentMode
      isRequired
    }
  }
`);

// Adding a row is what MAKES that document type required for completion — surfaced in the dialog
// copy so ops understands the consequence before confirming.
const AddChecklistItemDialogButton: FC<{
  requestId: string;
  availableTypes: UnitTransferDocumentTypeEnumType[];
}> = ({ requestId, availableTypes }) => {
  const [showDialog, toggleDialog] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [addChecklistItem, { loading, error }] = useMutation(
    ADD_UNIT_TRANSFER_CHECKLIST_ITEM_MUTATION,
    {
      refetchQueries: ['getUnitTransferRequestDocuments'],
    }
  );

  if (availableTypes.length === 0) return null;

  return (
    <>
      <Button variant="secondary" onClick={() => toggleDialog(true)}>
        Add Checklist Item
      </Button>
      <Dialog open={showDialog} onClose={() => toggleDialog(false)}>
        <DialogPanel>
          <DialogTitle>Add Checklist Item</DialogTitle>
          <p className="mb-4 text-sm text-gray-600">
            Adding a document type here makes it required — the transfer cannot be marked complete
            until it is uploaded and approved.
          </p>
          <FormPanel
            loading={loading}
            error={error}
            onCancel={() => toggleDialog(false)}
            onSubmit={data =>
              addChecklistItem({ variables: { requestId, type: data.type } }).then(() =>
                toggleDialog(false)
              )
            }
            buttonRef={buttonRef}
          >
            <FormInput
              type="select"
              fieldName="type"
              label="Document Type"
              defaultValue=""
              options={availableTypes.map(type => ({
                value: type,
                label: unitTransferDocumentTypeToReadable(type),
              }))}
            />
          </FormPanel>
          <DialogActions>
            <Button
              variant="secondary"
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.cancel();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.submit();
              }}
              loading={loading}
            >
              Add
            </Button>
          </DialogActions>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default AddChecklistItemDialogButton;
